<template>
    <KendoTimePicker v-model="innerValue"
                     :format="format ?? 'HH:mm'"
                     :id="id"
                     :name="name"
                     :placeholder="placeholder"
                     :readonly="readonly ?? false"
                     :disabled="disabled ?? false"
                     :min="minProp"
                     :max="maxProp"
                     :steps="steps ?? undefined"
                     :z-index="zIndex ?? undefined"
                     @change="onChange">
    </KendoTimePicker>
</template>

<script setup>
    import { computed, ref, watch } from 'vue';
    import { TimePicker as KendoTimePicker } from '@progress/kendo-vue-dateinputs';

    const isValidDate = d => d instanceof Date && !isNaN(d.getTime());
    const toDateSafe = v => {
        if (v == null || v === '') return null;
        if (v instanceof Date) return isValidDate(v) ? v : null;
        if (typeof v === 'number') { const d = new Date(v); return isValidDate(d) ? d : null; }
        if (typeof v === 'string') {
            const ms = /\/Date\((\d+)\)\//.exec(v)?.[1]; if (ms) return new Date(Number(ms));
            const dIso = new Date(v); if (isValidDate(dIso)) return dIso;
            const m = v.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
            if (m) { const [_, Y, M, D, h, min, s] = m; const d = new Date(+Y, +M - 1, +D, +h, +min, s ? +s : 0); return isValidDate(d) ? d : null; }
            const d = new Date(v); return isValidDate(d) ? d : null;
        }
        return null;
    };
    const toOutput = (d, mode) => {
        if (!d || !isValidDate(d)) return null;
        if (mode === 'iso') return d.toISOString();
        if (mode === 'ms') return d.getTime();
        if (mode === 'dotnet') return `/Date(${d.getTime()})/`;
        return d; // 'date'
    };

    const props = defineProps({
        modelValue: { type: [Date, String, Number, null], default: null },
        output: { type: String, default: 'date' },
        format: { type: String, default: undefined },
        id: { type: String, default: undefined },
        name: { type: String, default: undefined },
        placeholder: { type: String, default: undefined },
        readonly: { type: Boolean, default: undefined },
        disabled: { type: Boolean, default: undefined },
        min: { type: [Date, String, Number, null], default: undefined },
        max: { type: [Date, String, Number, null], default: undefined },
        steps: { type: Object, default: undefined },
        zIndex: { type: Number, default: undefined }
    });

    const emit = defineEmits(['update:modelValue']);

    const innerValue = ref(toDateSafe(props.modelValue));
    const minDate = computed(() => toDateSafe(props.min));
    const maxDate = computed(() => toDateSafe(props.max));
    const minProp = computed(() => minDate.value ?? undefined);
    const maxProp = computed(() => maxDate.value ?? undefined);

    watch(() => props.modelValue, nv => { innerValue.value = toDateSafe(nv); });

    const onChange = e => {
        innerValue.value = e?.value ?? null;
        emit('update:modelValue', toOutput(innerValue.value, props.output));
    };
</script>
